import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router';
import { errorMessages } from '~/constants/errorMessages.ts';
import {
  GUARANTEES_APPLICATION_CONTRAGENT_QUERY_PARAM,
  guaranteesApplicationStatusConfig,
  guaranteesClientText,
} from '~/modules/guarantees/form/client/constants.ts';
import { getStringValue } from '~/modules/guarantees/utils.ts';
import {
  selectGuaranteesAdministrationRoles,
  selectGuaranteesAdministrationRolesStatus,
  selectGuaranteesCurrentApplication,
  selectGuaranteesCurrentApplicationError,
  selectGuaranteesCurrentApplicationStatus,
  selectGuaranteesPatchIssueGuaranteeError,
  selectGuaranteesPatchIssueGuaranteeStatus,
  selectGuaranteesSubmitApprovalLetterByUdbtfError,
  selectGuaranteesSubmitApprovalLetterByUdbtfStatus,
} from '~/store/guarantees/selectors.ts';
import {
  fetchGuaranteesAdministrationRoles,
  fetchGuaranteesApplication,
  patchGuaranteesIssueGuarantee,
  submitGuaranteesApprovalLetterByUdbtf,
} from '~/store/guarantees/thunks/applicationThunks.ts';
import {
  AdministrationRole,
  GuaranteesApplicationRouteId,
  GuaranteesApplicationStatus,
  GuaranteesFetchStatus,
  type TPatchGuaranteesIssueGuaranteePayload,
} from '~/store/guarantees/types.ts';
import { normalizeGuaranteesApplicationStatus } from '~/store/guarantees/utils.ts';
import type { AppDispatch } from '~/store/store.ts';
import { useApplicationRequestFeedback } from '../../shared/hooks/useApplicationRequestFeedback.ts';
import { useFreshGuaranteesRequestError } from '../../shared/hooks/useFreshGuaranteesRequestError.ts';
import { getCurrentRouteGuaranteesApplication } from '../../shared/utils/applicationIdentity.ts';
import {
  UDBTF_GUARANTEE_SCAN_COPY_GET_STARTED_PATH,
  UDBTF_GUARANTEE_SCAN_COPY_TASKS_PATH,
  UDBTF_GUARANTEE_SCAN_COPY_TEXT,
  udbtfGuaranteeScanCopyEditableStatuses,
} from './constants.ts';
import { useUdbtfGuaranteeScanCopyDocuments } from './useUdbtfGuaranteeScanCopyDocuments.ts';

export const useUdbtfGuaranteeScanCopyPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { applicationId = '' } = useParams();
  const contragentId =
    new URLSearchParams(location.search).get(GUARANTEES_APPLICATION_CONTRAGENT_QUERY_PARAM) ?? '';
  const application = useSelector(selectGuaranteesCurrentApplication);
  const applicationStatus = useSelector(selectGuaranteesCurrentApplicationStatus);
  const applicationError = useSelector(selectGuaranteesCurrentApplicationError);
  const patchIssueGuaranteeStatus = useSelector(selectGuaranteesPatchIssueGuaranteeStatus);
  const patchIssueGuaranteeError = useSelector(selectGuaranteesPatchIssueGuaranteeError);
  const submitApprovalLetterByUdbtfStatus = useSelector(
    selectGuaranteesSubmitApprovalLetterByUdbtfStatus
  );
  const submitApprovalLetterByUdbtfError = useSelector(
    selectGuaranteesSubmitApprovalLetterByUdbtfError
  );
  const administrationRoles = useSelector(selectGuaranteesAdministrationRoles);
  const administrationRolesStatus = useSelector(selectGuaranteesAdministrationRolesStatus);
  const hasUdbtfAccess = administrationRoles.includes(AdministrationRole.UBTF_ADMINISTRATOR);
  const { currentRouteApplication, isLoadedApplicationCurrent } =
    getCurrentRouteGuaranteesApplication(application, applicationId);
  const effectiveApplicationId =
    getStringValue(currentRouteApplication?.applicationId || currentRouteApplication?.id) ||
    applicationId;
  const applicationNumber =
    getStringValue(currentRouteApplication?.applicationNumber) ||
    getStringValue(currentRouteApplication?.root?.applicationNumber) ||
    effectiveApplicationId;
  const normalizedStatus = normalizeGuaranteesApplicationStatus(
    currentRouteApplication?.applicationStatus
  );
  const isEditableStatus = udbtfGuaranteeScanCopyEditableStatuses.has(normalizedStatus);
  const statusConfig =
    guaranteesApplicationStatusConfig[normalizedStatus] ??
    guaranteesApplicationStatusConfig[GuaranteesApplicationStatus.DRAFT];
  const isApplicationLoading =
    (applicationStatus === GuaranteesFetchStatus.Loading && !currentRouteApplication) ||
    (!!application && !isLoadedApplicationCurrent);
  const currentApplicationVersion =
    typeof currentRouteApplication?.version === 'number'
      ? currentRouteApplication.version
      : undefined;
  const isSavingIssueGuarantee = patchIssueGuaranteeStatus === GuaranteesFetchStatus.Loading;
  const isSubmittingApprovalLetterByUdbtf =
    submitApprovalLetterByUdbtfStatus === GuaranteesFetchStatus.Loading;
  const isUdbtfAccessLoading =
    administrationRolesStatus === GuaranteesFetchStatus.Idle ||
    administrationRolesStatus === GuaranteesFetchStatus.Loading;
  const isUdbtfAccessDenied = !isUdbtfAccessLoading && !hasUdbtfAccess;
  const {
    modalAlert,
    modalSuccess,
    setModalAlert,
    setModalSuccess,
    showGuaranteesRequestError,
    handleCloseAlertModal,
    handleCloseSuccessModal,
  } = useApplicationRequestFeedback({
    applicationId,
    contragentId,
    dispatch,
  });
  const {
    deletingDocumentId,
    handleAddDocument,
    handleDeleteUploadedDocument,
    handleOpenDocument,
    hasApplicationFilesLoaded,
    isApplicationFilesLoading,
    isPreviewLoading,
    previewUrl,
    uploadingDocumentId,
  } = useUdbtfGuaranteeScanCopyDocuments({
    applicationId,
    effectiveApplicationId,
    setModalAlert,
    showGuaranteesRequestError,
  });
  const redirectPath = !contragentId ? UDBTF_GUARANTEE_SCAN_COPY_GET_STARTED_PATH : '';
  const navigateToTasks = useCallback(() => {
    navigate(UDBTF_GUARANTEE_SCAN_COPY_TASKS_PATH);
  }, [navigate]);
  const headerProps = useMemo(
    () => ({
      applicationNumber,
      applicationNumberLabel: guaranteesClientText.applicationsNumberLabel,
      applicationIdLabel: guaranteesClientText.applicationIdLabel,
      backLabel: guaranteesClientText.backToApplicationsButton,
      contragentId,
      contragentIdLabel: guaranteesClientText.contragentIdLabel,
      effectiveApplicationId,
      onBack: navigateToTasks,
      statusConfig,
    }),
    [applicationNumber, contragentId, effectiveApplicationId, navigateToTasks, statusConfig]
  );
  const isDocumentActionsDisabled =
    !effectiveApplicationId ||
    !isEditableStatus ||
    isApplicationLoading ||
    isApplicationFilesLoading ||
    isPreviewLoading ||
    !!uploadingDocumentId ||
    !!deletingDocumentId ||
    isSavingIssueGuarantee ||
    isSubmittingApprovalLetterByUdbtf;
  const areIssueDetailsDisabled =
    !effectiveApplicationId ||
    currentApplicationVersion === undefined ||
    !isEditableStatus ||
    isApplicationLoading ||
    isSavingIssueGuarantee ||
    isSubmittingApprovalLetterByUdbtf;
  const isSubmitDisabled =
    !effectiveApplicationId ||
    currentApplicationVersion === undefined ||
    !isEditableStatus ||
    isUdbtfAccessDenied ||
    isApplicationLoading ||
    isApplicationFilesLoading ||
    isPreviewLoading ||
    !!uploadingDocumentId ||
    !!deletingDocumentId ||
    isSavingIssueGuarantee ||
    isSubmittingApprovalLetterByUdbtf;

  const handleCloseUdbtfAccessModal = useCallback(() => {
    handleCloseAlertModal();
    navigate(UDBTF_GUARANTEE_SCAN_COPY_TASKS_PATH, { replace: true });
  }, [handleCloseAlertModal, navigate]);

  const handleSaveIssueGuarantee = async (
    payload: TPatchGuaranteesIssueGuaranteePayload
  ): Promise<void> => {
    if (!effectiveApplicationId || currentApplicationVersion === undefined) {
      showGuaranteesRequestError({ message: errorMessages.SERVICE_UNAVAILABLE });
      return;
    }

    try {
      await dispatch(
        patchGuaranteesIssueGuarantee({
          applicationId: effectiveApplicationId,
          payload,
          version: currentApplicationVersion,
        })
      ).unwrap();
      await dispatch(
        fetchGuaranteesApplication({ contragentId, applicationId: effectiveApplicationId })
      ).unwrap();
      setModalSuccess({
        text: UDBTF_GUARANTEE_SCAN_COPY_TEXT.saveSuccessMessage,
        buttonValue: UDBTF_GUARANTEE_SCAN_COPY_TEXT.successCloseButton,
      });
    } catch (error) {
      showGuaranteesRequestError(error);
      throw error;
    }
  };

  const handleSubmitApprovalLetterByUdbtf = async (): Promise<void> => {
    if (!effectiveApplicationId || currentApplicationVersion === undefined) {
      showGuaranteesRequestError({ message: errorMessages.SERVICE_UNAVAILABLE });
      return;
    }

    try {
      await dispatch(
        submitGuaranteesApprovalLetterByUdbtf({
          applicationId: effectiveApplicationId,
          version: currentApplicationVersion,
        })
      ).unwrap();
      setModalSuccess({
        text: UDBTF_GUARANTEE_SCAN_COPY_TEXT.submitSuccessMessage,
        buttonValue: UDBTF_GUARANTEE_SCAN_COPY_TEXT.successCloseButton,
        onClick: navigateToTasks,
        onClose: navigateToTasks,
      });
    } catch (error) {
      showGuaranteesRequestError(error);
    }
  };

  useEffect(() => {
    if (administrationRolesStatus !== GuaranteesFetchStatus.Idle) {
      return;
    }

    void dispatch(fetchGuaranteesAdministrationRoles());
  }, [administrationRolesStatus, dispatch]);

  useEffect(() => {
    if (!contragentId || !applicationId || applicationId === GuaranteesApplicationRouteId.New) {
      return;
    }

    void dispatch(fetchGuaranteesApplication({ contragentId, applicationId }));
  }, [applicationId, contragentId, dispatch]);

  useFreshGuaranteesRequestError(
    applicationError || patchIssueGuaranteeError || submitApprovalLetterByUdbtfError,
    showGuaranteesRequestError
  );

  useEffect(() => {
    if (isUdbtfAccessDenied) {
      setModalAlert({ text: UDBTF_GUARANTEE_SCAN_COPY_TEXT.accessDeniedText });
    }
  }, [isUdbtfAccessDenied, setModalAlert]);

  return {
    application: currentRouteApplication,
    areIssueDetailsDisabled,
    deletingDocumentId,
    effectiveApplicationId,
    handleAddDocument,
    handleCloseAlertModal,
    handleCloseSuccessModal,
    handleCloseUdbtfAccessModal,
    handleDeleteUploadedDocument,
    handleOpenDocument,
    handleSaveIssueGuarantee,
    handleSubmitApprovalLetterByUdbtf,
    hasApplicationFilesLoaded,
    headerProps,
    isApplicationLoading,
    isDocumentActionsDisabled,
    isPreviewLoading,
    isSavingIssueGuarantee,
    isSubmitDisabled,
    isSubmittingApprovalLetterByUdbtf,
    isUdbtfAccessDenied,
    isUdbtfAccessLoading,
    modalAlert,
    modalSuccess,
    previewUrl,
    redirectPath,
    showGuaranteesRequestError,
    uploadingDocumentId,
  };
};
